import { motion } from 'framer-motion';
import { fadeInUp, fadeIn, scaleIn } from '../../lib/motion';

const VARIANTS = { fadeInUp, fadeIn, scaleIn };

/**
 * Substitui o par useScrollReveal + `className={isVisible ? 'animate-fade-in-up opacity-0' : 'opacity-0'}`
 * que se repetia em 7 seções da landing page. Mesma ideia — anima quando
 * entra na tela, uma vez só — mas como uma variant do framer-motion em
 * vez de classe CSS condicional, para usar a mesma curva/timing do
 * resto do site.
 *
 * Dois modos:
 *   mode="scroll" (padrão) — usa whileInView. Certo para a landing page:
 *     conteúdo comprido, o visitante rola até ele.
 *   mode="mount" — usa animate direto, sem IntersectionObserver. Certo
 *     para wizard/formulário/telas de resultado: telas de app de tamanho
 *     único, sem narrativa de rolagem. whileInView aqui causava um bug
 *     real — dentro do layout com overflow-y-auto do funil, a primeira
 *     pergunta do wizard nascia com opacity:0 e nunca disparava (o
 *     IntersectionObserver falhava a checagem de visibilidade e, com
 *     once:true, nunca tentava de novo). Reproduzido em produção: F5
 *     "consertava" porque forçava uma montagem nova com o layout já
 *     estável; navegar normalmente reaproveitava o componente e travava.
 *
 * Uso:
 *   <Reveal><h2>Título da seção</h2></Reveal>
 *   <Reveal delay={0.2} variant="scaleIn"><Card /></Reveal>
 *   <Reveal mode="mount" className="...">Tela de wizard/formulário</Reveal>
 */
export const Reveal = ({
    children,
    variant = 'fadeInUp',
    delay = 0,
    as = 'div',
    className = '',
    mode = 'scroll',
    once = true,
    amount = 0.2,
    ...rest
}) => {
    const Tag = motion[as] || motion.div;
    const chosen = VARIANTS[variant] || fadeInUp;
    const variants = delay
        ? {
            hidden: chosen.hidden,
            visible: { ...chosen.visible, transition: { ...chosen.visible.transition, delay } },
        }
        : chosen;

    const modeProps = mode === 'mount'
        ? { initial: 'hidden', animate: 'visible' }
        : { initial: 'hidden', whileInView: 'visible', viewport: { once, amount } };

    return (
        <Tag className={className} variants={variants} {...modeProps} {...rest}>
            {children}
        </Tag>
    );
};
